import type { MultilineContent } from "@repo/app-types/content/types";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type {
  PlaybookCriterionNode,
  PlaybookId,
  PlaybookNodeId,
  PlaybookSectionNode,
  PlaybookTree,
} from "@repo/app-types/entities/playbook";
import type { FeedbackWithCurrentGrade } from "@repo/app-types/entities/recordingAssessmentFeedback";
import { type FC, useCallback } from "react";
import type { PlaybookEditorData } from "../hooks/usePlaybookEditor.js";
import type { UseSampleToolResultsResult } from "../hooks/useSampleToolResults.js";
import { RecordingAssessmentEditor } from "./recordingAssessmentEditor.js";
import type { AssessmentTreeItemKey } from "./recordingAssessmentEditor/assessmentTreeHelpers.js";

export interface PlaybookEditorContainerProps {
  /** The current playbook data (controlled) */
  data: PlaybookEditorData | undefined;

  /** The initial outcome value (used to show change indicator) */
  initialOutcome: OutcomeDefinition | null | undefined;

  /** The live/published outcome for change comparison (draft dot + context diff). */
  liveOutcome?: OutcomeDefinition | null;

  /** The live/published tree for change comparison (used to show edit indicators) */
  liveTree?: PlaybookTree;

  /** Feedback data for displaying accuracy indicators */
  feedback: readonly FeedbackWithCurrentGrade[];

  /** Whether the editor is read-only */
  isReadOnly?: boolean;

  /** If provided and present in the tree, pre-select this criterion on mount. */
  initialSelectedCriterionId?: string;

  /** Callback when data changes */
  onChange: (changes: Partial<PlaybookEditorData>) => void;

  /** Callback when the user wants AI help optimizing a criterion */
  onOptimizeCriterion?: (criteria: PlaybookCriterionNode) => void;

  /** Callback when the user wants the AI to run the sample-distribution tool against a criterion as-is. */
  onTestCriterion?: (criteria: PlaybookCriterionNode) => void;

  /** Callback when the user wants AI help optimizing a section */
  onOptimizeSection?: (section: PlaybookSectionNode) => void;

  /** Callback when the user wants the AI to run the sample-applicability tool against a section as-is. */
  onTestSection?: (section: PlaybookSectionNode) => void;

  /** Callback when the user wants AI help optimizing the outcome rubric */
  onOptimizeOutcome?: (outcome: OutcomeDefinition) => void;

  /** Callback when the user wants the AI to run the sample-outcome tool against the outcome as-is. */
  onTestOutcome?: (outcome: OutcomeDefinition) => void;

  /** Whether a criterion, section, or outcome optimization is in progress */
  isOptimizingSectionOrCriterion?: boolean;

  /**
   * Sample-tool results collected during this editing session. The map is
   * keyed by `sample_tool_result_id`; consumers look up an entry by the
   * currently-selected criterion's `playbook_node_id`.
   */
  sampleToolResults?: UseSampleToolResultsResult["results"];

  /** Drop the saved sample-tool result for the given criterion id. */
  onDiscardSampleToolResultForNode?: (playbookNodeId: PlaybookNodeId) => void;
  playbookId?: PlaybookId | null;

  /**
   * Selection signal from the assistant sidebar. When the identity (object
   * reference) changes, the assessment editor selects the referenced
   * criterion. The `nonce` lets the same id re-fire selection. `kind`
   * disambiguates existing criteria (selected via the `criteria` tree key)
   * from `new_criterion` proposals (selected via `ghost-criterion`).
   */
  criterionSelectionRequest?: {
    id: PlaybookNodeId;
    kind:
      | "existing_criterion"
      | "new_criterion"
      | "existing_section"
      | "new_section"
      | "outcome";
    nonce: number;
  } | null;
  /** Reports the editor's current selection up to flag the "Viewing" row. */
  onCurrentSelectionChange?: (key: AssessmentTreeItemKey | undefined) => void;
}

export const PlaybookEditorContainer: FC<PlaybookEditorContainerProps> = ({
  data,
  initialOutcome,
  liveOutcome,
  liveTree,
  feedback,
  isReadOnly = false,
  initialSelectedCriterionId,
  onChange,
  onOptimizeCriterion,
  onTestCriterion,
  onOptimizeSection,
  onTestSection,
  onOptimizeOutcome,
  onTestOutcome,
  isOptimizingSectionOrCriterion = false,
  sampleToolResults,
  onDiscardSampleToolResultForNode,
  playbookId,
  criterionSelectionRequest,
  onCurrentSelectionChange,
}) => {
  // Handlers that delegate to onChange
  const handleTreeChange = useCallback(
    (template: PlaybookTree) => {
      onChange({ tree: template });
    },
    [onChange],
  );

  const handleOutcomeChange = useCallback(
    (outcome: OutcomeDefinition | null) => {
      onChange({ outcome });
    },
    [onChange],
  );

  const handleSummaryTemplateBodyChange = useCallback(
    (summaryTemplateBody: MultilineContent | null) => {
      onChange({ summaryTemplateBody });
    },
    [onChange],
  );

  // Show loading or empty state
  if (!data || initialOutcome === undefined) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border border-border-light bg-gray-50 text-muted-foreground">
        <p className="text-sm">Loading playbook...</p>
      </div>
    );
  }

  return (
    <RecordingAssessmentEditor
      tree={data.tree}
      liveTree={liveTree}
      feedback={feedback}
      onChange={handleTreeChange}
      isReadOnly={isReadOnly}
      outcomeHandler={{
        outcome: data.outcome,
        initialOutcome,
        liveOutcome,
        onChange: handleOutcomeChange,
      }}
      summaryTemplateHandler={{
        body: data.summaryTemplateBody,
        onChange: handleSummaryTemplateBodyChange,
      }}
      initialSelectedCriterionId={initialSelectedCriterionId}
      onOptimizeCriterion={onOptimizeCriterion}
      onTestCriterion={onTestCriterion}
      onOptimizeSection={onOptimizeSection}
      onTestSection={onTestSection}
      onOptimizeOutcome={onOptimizeOutcome}
      onTestOutcome={onTestOutcome}
      isOptimizingSectionOrCriterion={isOptimizingSectionOrCriterion}
      sampleToolResults={sampleToolResults}
      onDiscardSampleToolResultForNode={onDiscardSampleToolResultForNode}
      playbookId={playbookId}
      criterionSelectionRequest={criterionSelectionRequest}
      onCurrentSelectionChange={onCurrentSelectionChange}
    />
  );
};
